import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { overrideStarterApiNames, useShowStarterCustomerPlatformUpsellCard } from '../../hooks/upsell/useStarterUpsell';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { getNavItemForProductLine } from '../../utils/routingUtils';
import { CustomError } from '../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../v2/components/error-boundary/withErrorBoundary';
import { productLineToSkuCategoryName } from '../../v2/constants';
import { PinnedFeaturesConfig } from '../../v2/constants/PinnedFeaturesConfig';
import { useBadges } from '../../v2/hooks/badges/useBadges';
import { usePinnedFeaturesBySkuCategoryName } from '../../v2/hooks/features/useFeatures';
import { trackFeatureTableInteraction } from '../../v2/utils/tracking/trackingEventUtils';
import ProductFeatureTableHeaderCell from './product-feature-table/ProductFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const buildFeatureTableHeaderData = ({
  filteredHighlightedProducts,
  badges
}) => {
  return filteredHighlightedProducts.reduce((headerDataMap, apiName) => {
    var _badges$apiName;
    headerDataMap[apiName] = {
      Content: /*#__PURE__*/_jsx(ProductFeatureTableHeaderCell, {
        apiName: apiName,
        badgeName: badges === null || badges === void 0 || (_badges$apiName = badges[apiName]) === null || _badges$apiName === void 0 ? void 0 : _badges$apiName.badgeName
      })
    };
    return headerDataMap;
  }, {});
};
const buildFeatureTableBodyData = ({
  features,
  productLine,
  highlightedProducts,
  screen,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen: screen,
      staticDisplayRowCount
    },
    sectionData: {
      [productLine]: {
        apiNames: highlightedProducts,
        features: features || [],
        LabelContent: /*#__PURE__*/_jsx(_Fragment, {})
      }
    }
  };
};
const ProductFeatureTable = ({
  productLine,
  highlightedProducts,
  screen,
  recommendedProductLevel = 'PROFESSIONAL'
}) => {
  var _PinnedFeaturesConfig;
  const skuCategoryName = productLineToSkuCategoryName[productLine];
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const navItem = getNavItemForProductLine(productLine);
  const {
    showStarterCustomerPlatformUpsellCard,
    upsellStarterApiName
  } = useShowStarterCustomerPlatformUpsellCard();
  const productApiNamesForBadges = overrideStarterApiNames(highlightedProducts, showStarterCustomerPlatformUpsellCard, upsellStarterApiName);
  const {
    badges
  } = useBadges({
    productApiNames: productApiNamesForBadges
  });
  const showRecommended = Object.values(badges !== null && badges !== void 0 ? badges : {}).some(badge => {
    return badge && badge.badgeName === 'RECOMMENDED';
  }) || false;
  const {
    featuresBySkuCategoryName,
    loading,
    error
  } = usePinnedFeaturesBySkuCategoryName({
    [skuCategoryName]: highlightedProducts
  });
  const features = featuresBySkuCategoryName[skuCategoryName];
  const {
    showBoundary
  } = useErrorBoundary();
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError(error.message || 'Error fetching features', {
      error,
      apiNames: highlightedProducts,
      productLine,
      fingerprint: 'errorFetchingFeatures'
    }));
  }
  if (!features || !features.length) return null;

  // Map upsell badge back to original starter api key for header rendering
  const badgesForHeader = Object.assign({}, badges);
  if (showStarterCustomerPlatformUpsellCard) {
    const starterApiName = highlightedProducts.find(api => api.endsWith('-starter'));
    if (starterApiName) {
      var _badges$upsellStarter;
      badgesForHeader[starterApiName] = (_badges$upsellStarter = badges === null || badges === void 0 ? void 0 : badges[upsellStarterApiName]) !== null && _badges$upsellStarter !== void 0 ? _badges$upsellStarter : null;
    }
  }
  const headerData = buildFeatureTableHeaderData({
    filteredHighlightedProducts: highlightedProducts,
    badges: badgesForHeader
  });
  const bodyData = buildFeatureTableBodyData({
    features,
    productLine,
    highlightedProducts,
    screen: screen !== null && screen !== void 0 ? screen : navItem,
    staticDisplayRowCount: Math.min(((_PinnedFeaturesConfig = PinnedFeaturesConfig[skuCategoryName]) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length) || 5, 5)
  });
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: isSeatsRedesignUngated,
    onExpand: () => {
      trackFeatureTableInteraction({
        action: 'clicked-expand-table',
        value: getI18nText('upgrades.featureTable.body.expand', {
          locale: 'en-us'
        }),
        productLine,
        productNames: highlightedProducts,
        recommendedPlan: recommendedProductLevel,
        showRecommended
      });
    },
    onCollapse: () => {
      trackFeatureTableInteraction({
        action: 'clicked-collapse-table',
        value: getI18nText('upgrades.featureTable.body.collapse', {
          locale: 'en-us'
        }),
        productLine,
        productNames: highlightedProducts,
        recommendedPlan: recommendedProductLevel,
        showRecommended
      });
    },
    isInbound25PricingPage: true
  });
};
export default withErrorBoundary(ProductFeatureTable, {
  fallback: null,
  errorBoundaryName: 'PRODUCT_FEATURE_TABLE'
});